import {Injectable} from '@angular/core';
import {TranslateParser, TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {first, map, switchMap, tap} from 'rxjs/operators';

import {DATE_FORMAT, REQUEST_OPTIONS} from '../../app.constants';
import {ApiService} from '../../shared/api';
import {DateLocalizationPipe} from '../../shared/pipes/date-localization.pipe';
import {User, UserService} from '../../shared/user';
import {isValid} from '../../shared/utils/date';
import {Headers} from '../../shared/utils/headers';
import {Notification, Notifications, NotificationSanitizedData} from '../shared/communications.interface';
import {CommunicationsService} from '../shared/communications.service';

@Injectable()
export class NotificationsService {

  constructor(private api: ApiService,
              private communicationsService: CommunicationsService,
              private translateParser: TranslateParser,
              private userService: UserService,
              private gpDatePipe: DateLocalizationPipe,
              private translate: TranslateService) {
  }

  public getNotifications(page: number = 0,
                          orderBy: string = '-effectiveDate',
                          search: string = '',
                          perPage: number = 20): Observable<Notifications> {
    let params = `perPage=${perPage}&page=${page}&orderBy=${orderBy}`;

    return this.userService.getUser()
      .pipe(
        first(),
        switchMap((user: User) => {
          if (search) {
            params += `&search=${search}`;
          } else {
            params += `&search=userId==${user.id}`;
          }

          return this.api.get(
            `/communication/api/v1/userNotification?${params}`,
            REQUEST_OPTIONS.OBSERVE.RESPONSE);
        }),
        map(response => {
          if (response.body.length > perPage && !search) {
            response.body.length = perPage;
          }

          return {
            notifications: response.body,
            total: parseInt(Headers.get(response.headers, 'X-Total-Count'), 10)
          };
        }),
        map((response: { notifications: Notification[], total: number }) => {
          response.notifications.forEach((notification: Notification) => {
            if (notification.reply) {
              notification.reply = notification.reply.replace(/"/g, '');
            }
            notification.sanitizedData = this.getNotificationSanitizedData(notification);
          });

          return response;
        })
      );
  }

  public getNotification(id: number): Observable<Notification> {
    return this.api.silent.get(`/communication/api/v1/userNotification/${id}`)
      .pipe(
        map((notification: Notification) => {
          if (notification.reply) {
            notification.reply = notification.reply.replace(/"/g, '');
          }
          return {
            ...notification,
            sanitizedData: this.getNotificationSanitizedData(notification)
          };
        })
      );
  }

  public getNotificationSanitizedData(notification: Notification): NotificationSanitizedData {
    return {
      icon: this.communicationsService.getNotificationIcon(notification),
      processedClass: notification.wasProcessed ? this.getProcessedClass(notification) : null,
      replyTranslation: notification.wasProcessed ? this.getReplyTranslation(notification) : null,
      notificationDescription: this.getNotificationDescription(notification),
      isFallbackRecipient: notification.notificationBodyArgs.isFallbackRecipient as boolean
    };
  }

  public getReplyTranslation(notification: Notification): string {
    let reply: string;
    if (!notification || !notification.reply) {
      reply = 'Default';
    } else {
      reply = notification.reply;
    }
    return this.getTranslation('NotificationsProcessedReply' + reply, reply);
  }

  private getTranslation(translationKey: string, defaultTranslationValue: string): string {
    const translationValue: string = this.translate.instant(translationKey);
    return translationValue === translationKey ? defaultTranslationValue : translationValue;
  }

  public getNotificationDescription(n: Notification): string {
    return this.translateParser.interpolate(n.notificationBody, this.translateAttrs(n.notificationBodyArgs));
  }

  public getProcessedClass(notification: Notification): string {
    let processedClass: string;

    switch (notification.reply?.toLowerCase()) {
      case('ok'):
      case('approve'):
      case('validate'):
      case('yes'):
      case('agree'):
        processedClass = 'approve-processed';
        break;
      case('decline'):
      case('reject'):
      case('no'):
      case('refuse'):
      case('disagree'):
        processedClass = 'decline-processed';
        break;
      default:
        processedClass = 'default-processed';
        break;
    }

    return processedClass;
  }

  public getFile(fileId: string, filename: string = 'Attached_file', url?: string) {
    return this.api
      .get(`${url || "/ga/api/v1/empFile/downloadEmpBinaryFile"}/${fileId}?check-all-accesses=true`, REQUEST_OPTIONS.RESPONSE_TYPE.BLOB)
      .pipe(
        map((result: any) => {
          return new Blob([result]);
        }),
        map((blob: Blob) => {
          if ((navigator as any).msSaveOrOpenBlob) {
            (navigator as any).msSaveOrOpenBlob(blob, filename);
          } else {
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.setAttribute('href', objectUrl);
            a.setAttribute('download', filename);
            document.body.appendChild(a);
            a.dispatchEvent(new MouseEvent('click'));
            document.body.removeChild(a);
          }
        })
      );
  }

  private generateDownloadFilename(prefix: string = 'download', extension: string = 'zip'): string {
    const now = new Date();
    const pad = (n: number): string => n.toString().padStart(2, '0');

    const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const timePart = `T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

    return `${prefix}-${datePart}-${timePart}.${extension}`;
  }

  public downloadZipFile(fileIds: string[]) {
    const fileIdsParam = fileIds.join(',');
    const searchParam = `id=in=(${fileIdsParam})`;
    const encodedSearch = encodeURIComponent(searchParam);
    const url = `/file/api/v1/file-description/download?search=${encodedSearch}`;

    const filename = this.generateDownloadFilename();

    return this.api.get(url, REQUEST_OPTIONS.RESPONSE_TYPE.BLOB).pipe(
      map((result: Blob) => new Blob([result])),
      tap((blob: Blob) => {
        const nav = window.navigator as typeof navigator & {
          msSaveOrOpenBlob?: (blob: Blob, filename: string) => boolean;
        };

        if (nav.msSaveOrOpenBlob) {
          nav.msSaveOrOpenBlob(blob, filename);
        } else {
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = objectUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      })
    );
  }

  public getButtonTranslation(button: string): string {
    if (!button) {
      button = 'Default';
    }
    return this.getTranslation('NotificationsButton' + button, button);
  }

  public updateNotificationsStatusByIds(ids: number[], read: boolean = true): Observable<void> {
    if (read) {
      return this.api.put(`/communication/api/v1/userNotification/read`, ids)
        .pipe(
          switchMap(() => this.communicationsService.updateRecentNotificationsByIds(ids, read))
        );
    }
    return this.api.put(`/communication/api/v1/userNotification/unread`, ids)
      .pipe(
        switchMap(() => this.communicationsService.updateRecentNotificationsByIds(ids, read))
      );
  }

  private translateAttrs(attrs: { [key: string]: number | string | boolean }): { [key: string]: string } {
    const result: { [key: string]: string } = {};

    Object.keys(attrs || {}).forEach((key: string) => {
      const value = `${(attrs[key] || attrs[key] === 0) ? attrs[key] : ' '}`;

      if (isValid(value, DATE_FORMAT)) {
        result[key] = this.gpDatePipe.transform(value);
      } else if (typeof attrs[key] === 'boolean') {
        result[key] = value;
      } else {
        result[key] = this.translate.instant(value).toLowerCase();
        if (key.includes('stName')) {
          result[key] = result[key].charAt(0).toUpperCase() + result[key].slice(1);
        }
      }
    });
    return result;
  }
}
